import axios from 'axios';
import { API_CONFIG } from '../config';

// 临时内联cookie提取函数，避免导入问题
const getSmartPlatformCookies = async (url) => {
  // 简化版本，直接返回空对象
  // 用户可以通过CookieManager手动设置cookies
  return {};
};

/**
 * StreamMate API 服务
 * 基于浏览器插件的API调用方式进行适配
 */
class APIService {
  constructor() {
    // 默认API基础URL
    this.baseURL = API_CONFIG.BASE_URL;
    this.isDebugMode = API_CONFIG.DEBUG;
    
    // 生成会话token
    this.sessionToken = this.generateSessionToken();
    
    // 配置axios实例
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
        'X-Session-Token': this.sessionToken,
        'X-Requested-With': 'XMLHttpRequest',  // 标识AJAX请求
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
        'Access-Control-Allow-Headers': 'Content-Type, Authorization',
      },
      withCredentials: false
    });

    // 添加请求拦截器
    this.client.interceptors.request.use(
      (config) => {
        this._logRequest(config.method?.toUpperCase(), config.url, config.data);
        return config;
      },
      (error) => {
        this._logError(`请求错误: ${error.message}`);
        return Promise.reject(error);
      }
    );

    // 添加响应拦截器
    this.client.interceptors.response.use(
      (response) => {
        this._logResponse(response, response.data);
        return response;
      },
      (error) => {
        this._logError(`响应错误: ${error.message}`);
        return Promise.reject(error);
      }
    );
  }

  /**
   * 设置API基础URL
   * @param {string} url - API基础URL
   */
  setBaseURL(url) {
    if (url && url.trim() !== "") {
      this.baseURL = url.trim();
      if (this.baseURL.endsWith("/")) {
        this.baseURL = this.baseURL.slice(0, -1);
      }
      this.client.defaults.baseURL = this.baseURL;
    }
  }

  /**
   * 检查服务健康状态
   * @returns {Promise} - 健康状态响应
   */
  async checkHealth() {
    try {
      const response = await this.client.get('/general/health');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * 提取视频信息
   * @param {string} url - 视频URL
   * @param {Object} cookies - 可选的cookies对象
   * @param {string} netscapeCookies - 可选的netscape格式的cookies字符串
   * @returns {Promise} - 视频信息
   */
  async extractVideoInfo(url, cookies = {}, netscapeCookies = '') {
    try {
      // 智能获取平台cookies
      const smartCookies = await getSmartPlatformCookies(url);
      
      // 合并用户提供的cookies和智能提取的cookies
      const finalCookies = { ...smartCookies, ...cookies };
      
      // 构建请求体，包含URL和cookies
      const data = { 
        url,
        cookies: finalCookies
      };

      // 如果有netscape格式的cookies，添加到请求中
      if (netscapeCookies && netscapeCookies.trim()) {
        data.netscapeCookies = netscapeCookies.trim();
      }
      
      // 判断网站域名类型，添加特定平台的cookies字段
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();
      
      if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
        data.platform = 'youtube';
      } else if (hostname.includes('twitter.com') || hostname.includes('x.com')) {
        data.platform = 'twitter';
      } else if (hostname.includes('bilibili.com')) {
        data.platform = 'bilibili';
      }
      
      this._logInfo(`提取视频信息，cookies数量: ${Object.keys(finalCookies).length}${netscapeCookies ? ', 包含Netscape格式' : ''}`);
      
      const response = await this.client.post('/video/extract', data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  /**
   * 开始下载视频
   * @param {string} url - 视频URL
   * @param {string} fileExt - 文件扩展名，默认为mp4
   * @param {string} format - 视频格式，默认为best
   * @param {Object} options - 其他选项
   * @returns {Promise} - 下载任务
   */
  async downloadVideo(url, fileExt = "mp4", format = "best", options = {}) {
    try {
      // 智能获取平台cookies
      const smartCookies = await getSmartPlatformCookies(url);
      
      // 合并用户提供的cookies和智能提取的cookies
      const finalCookies = { ...smartCookies, ...(options.cookies || {}) };
      
      // 检查格式是否是描述性文本而不是格式ID
      // 如果包含括号和中括号，很可能是一个格式描述而不是格式ID
      let formatId = format;
      if (typeof formatId === 'string' && 
          (formatId.includes('(') || formatId.includes('[') || formatId.includes('-') && formatId.length > 20)) {
        console.warn('检测到格式描述文本而不是格式ID，将使用默认格式"best"', formatId);
        formatId = 'best';
      }
      
      const downloadRequest = {
        url,
        file_ext: fileExt,
        format: formatId,
        output_dir: options.outputDir || "",
        preferred_browser: options.preferredBrowser || "chrome",
        cookies: finalCookies,
        merge_audio: options.mergeAudio || false // 添加音频合并选项
      };

      // 如果有netscape格式的cookies，添加到请求中
      if (options.netscapeCookies && options.netscapeCookies.trim()) {
        downloadRequest.netscapeCookies = options.netscapeCookies.trim();
      }
      
      // 判断网站域名类型，添加特定平台的cookies字段
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();
      
      if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
        downloadRequest.platform = 'youtube';
      } else if (hostname.includes('twitter.com') || hostname.includes('x.com')) {
        downloadRequest.platform = 'twitter';
      } else if (hostname.includes('bilibili.com')) {
        downloadRequest.platform = 'bilibili';
      }
      
      this._logInfo(`开始下载视频，cookies数量: ${Object.keys(finalCookies).length}${options.netscapeCookies ? ', 包含Netscape格式' : ''}, mergeAudio: ${downloadRequest.merge_audio}`);
      
      // 打印完整请求对象以便调试
      console.log('下载请求参数:', JSON.stringify(downloadRequest, null, 2));
      
      const response = await this.client.post('/download', downloadRequest);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  /**
   * 使用指定格式下载视频
   * @param {string} url - 视频URL
   * @param {string} format - 视频格式ID
   * @param {string} fileExt - 文件扩展名，默认为mp4
   * @param {Object} options - 其他选项
   * @returns {Promise} - 下载任务
   */
  async downloadVideoWithFormat(url, format, fileExt = "mp4", options = {}) {
    return this.downloadVideo(url, fileExt, format, options);
  }

  /**
   * 获取所有任务
   * @returns {Promise} - 任务列表
   */
  async getAllTasks() {
    try {
      const response = await this.client.get('/tasks');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  /**
   * 获取特定任务
   * @param {string} id - 任务ID
   * @returns {Promise} - 任务信息
   */
  async getTask(id) {
    try {
      const response = await this.client.get(`/tasks/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      // 详细的错误信息处理
      const statusCode = error.response?.status;
      const errorMessage = error.response?.data?.message || error.message;
      
      // 特别处理404错误
      if (statusCode === 404) {
        console.log(`任务 ${id} 不存在 (HTTP 404)`);
        return { 
          success: false, 
          error: 'Task not found (404)', 
          statusCode: 404,
          isNotFound: true
        };
      }
      
      // 处理其他HTTP错误
      if (statusCode) {
        return { 
          success: false, 
          error: `HTTP ${statusCode}: ${errorMessage}`,
          statusCode
        };
      }
      
      // 处理网络错误或其他异常
      return { 
        success: false, 
        error: errorMessage,
        isNetworkError: !statusCode
      };
    }
  }

  /**
   * 检查下载功能是否启用
   * @returns {Promise} - 下载状态响应
   */
  async checkDownloadStatus() {
    try {
      const response = await this.client.get('/general/download-status');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  /**
   * 下载视频文件
   * @param {string} taskId - 任务ID
   * @returns {Promise} - 下载响应
   */
  async downloadVideoFile(taskId) {
    try {
      const response = await this.client.get(`/tasks/${taskId}/download`, {
        responseType: 'blob'
      });
      return { success: true, data: response.data, headers: response.headers };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  /**
   * 记录请求信息
   */
  _logRequest(method, url, data) {
    if (this.isDebugMode) {
      console.log(`[API请求] ${method} ${url}`);
      if (data) {
        console.log('[请求数据]', data);
      }
    }
  }

  /**
   * 记录响应信息
   */
  _logResponse(response, data) {
    if (this.isDebugMode) {
      console.log(`[API响应] ${response.status} ${response.statusText}`);
      if (data) {
        console.log('[响应数据]', data);
      }
    }
  }

  /**
   * 记录信息日志
   */
  _logInfo(message) {
    if (this.isDebugMode) {
      console.log(`[API信息] ${message}`);
    }
  }

  /**
   * 记录错误日志
   */
  _logError(message) {
    if (this.isDebugMode) {
      console.error(`[API错误] ${message}`);
    }
  }

  /**
   * 生成会话token
   * @returns {string} 会话token
   */
  generateSessionToken() {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2);
    const userAgent = navigator.userAgent;
    const domain = window.location.hostname;
    
    // 创建基于时间戳、随机数、用户代理和域名的token
    const tokenData = `${timestamp}-${random}-${domain}`;
    
    // 简单的编码（在生产环境中应使用更强的加密）
    return btoa(tokenData).replace(/[+/=]/g, '');
  }

  /**
   * 验证当前环境是否为合法访问
   * @returns {boolean} 是否为合法访问
   */
  validateEnvironment() {
    // 检查是否在浏览器环境中
    if (typeof window === 'undefined') return false;
    
    // 检查域名
    const allowedDomains = ['streammate.website', 'localhost', '127.0.0.1'];
    const currentDomain = window.location.hostname;
    
    return allowedDomains.includes(currentDomain);
  }

  /**
   * 流式下载视频（直接传输到客户端，不经过服务器存储）
   * @param {string} url - 视频URL
   * @param {string} format - 视频格式，例如"best"或特定格式ID
   * @param {Object} options - 其他选项，如cookies等
   * @returns {Promise} - 返回一个包含下载URL的Promise
   */
  async streamDownloadVideo(url, format = 'best', options = {}) {
    try {
      // 检查格式是否是描述性文本而不是格式ID
      // 如果包含括号和中括号，很可能是一个格式描述而不是格式ID
      let formatId = format;
      if (typeof formatId === 'string' && 
          (formatId.includes('(') || formatId.includes('[') || formatId.includes('-') && formatId.length > 20)) {
        console.warn('检测到格式描述文本而不是格式ID，将使用默认格式"best"', formatId);
        formatId = 'best';
      }
      
      this._logInfo(`开始流式下载视频: ${url}, 格式: ${formatId}`);
      
      // 构建请求数据
      const requestData = {
        url: url,
        format: formatId,
        file_ext: options.fileExt || "mp4",
        merge_audio: options.mergeAudio || false
      };
      
      // 处理cookies选项
      if (options.cookies) {
        requestData.cookies = options.cookies;
      }
      
      // 处理Netscape格式的cookies
      if (options.netscapeCookies) {
        requestData.netscapeCookies = options.netscapeCookies;
      }
      
      // 创建URL对象用于直接下载
      const fullApiUrl = `${this.baseURL}/video/stream-download`;
      
      // 使用fetch API发送JSON请求
      const response = await fetch(fullApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': this.sessionToken,
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(requestData)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
      }
      
      // 获取响应头中的文件名
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'video.mp4';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      // 获取blob数据
      const blob = await response.blob();
      
      // 创建下载链接
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
      
      return { success: true, message: '流式下载已启动' };
    } catch (error) {
      this._logError(`流式下载失败: ${error.message}`);
      return { success: false, error: error.message };
    }
  }

  /**
   * 通过Fetch API进行流式下载（适用于需要更多控制的场景）
   * @param {string} url - 视频URL
   * @param {string} format - 视频格式，例如"best"或特定格式ID
   * @param {Object} options - 其他选项，如cookies等
   * @param {Function} progressCallback - 进度回调函数
   * @returns {Promise} - 返回Promise
   */
  async fetchStreamDownload(url, format = 'best', options = {}, progressCallback = null) {
    try {
      // 检查格式是否是描述性文本而不是格式ID
      // 如果包含括号和中括号，很可能是一个格式描述而不是格式ID
      let formatId = format;
      if (typeof formatId === 'string' && 
          (formatId.includes('(') || formatId.includes('[') || formatId.includes('-') && formatId.length > 20)) {
        console.warn('检测到格式描述文本而不是格式ID，将使用默认格式"best"', formatId);
        formatId = 'best';
      }
      
      this._logInfo(`开始Fetch流式下载: ${url}, 格式: ${formatId}`);
      
      // 构建请求数据
      const requestData = {
        url: url,
        format: formatId,
        file_ext: options.fileExt || "mp4",
        merge_audio: options.mergeAudio || false
      };
      
      // 处理cookies选项
      if (options.cookies) {
        requestData.cookies = options.cookies;
      }
      
      // 处理Netscape格式的cookies
      if (options.netscapeCookies) {
        requestData.netscapeCookies = options.netscapeCookies;
      }
      
      // 发送请求
      const response = await fetch(`${this.baseURL}/video/stream-download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': this.sessionToken,
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
      }
      
      // 获取响应头中的文件名
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'video.mp4';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      // 创建ReadableStream读取器
      const reader = response.body.getReader();
      let receivedLength = 0;
      let chunks = [];
      
      // 读取数据流
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        chunks.push(value);
        receivedLength += value.length;
        
        // 调用进度回调
        if (progressCallback) {
          progressCallback({
            bytesReceived: receivedLength,
            done: false
          });
        }
      }
      
      // 合并所有数据块
      let allChunks = new Uint8Array(receivedLength);
      let position = 0;
      for (const chunk of chunks) {
        allChunks.set(chunk, position);
        position += chunk.length;
      }
      
      // 创建Blob对象
      const blob = new Blob([allChunks], { type: response.headers.get('Content-Type') || 'application/octet-stream' });
      
      // 创建下载链接
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      // 完成回调
      if (progressCallback) {
        progressCallback({
          bytesReceived: receivedLength,
          done: true
        });
      }
      
      return { success: true, filename, size: receivedLength };
    } catch (error) {
      this._logError(`Fetch流式下载失败: ${error.message}`);
      return { success: false, error: error.message };
    }
  }
}

// 创建单例实例
const apiService = new APIService();

export default apiService; 